import type { JsonServiceClient } from "@servicestack/client";

import {
    BeginTemporaryFileParallelChunkRequest,
    GetTemporaryFileParallelChunkStatusRequest,
    RemoveTemporaryFileParallelChunkRequest
} from "src/api/Tmd.dtos";

import { isRetryableChunkUploadStatus } from "./ChunkUploadRetryPolicy";

type ChunkedUploadState = {
    isPaused: boolean;
    activeControllers: Set<AbortController>;
    cancellationPromise?: Promise<void>;
    completedChunksCount: number;
    totalChunksCount: number;
    state: "idle" | "uploading" | "paused" | "cancelling" | "completed" | "failed" | "cancelled";
    progress: number;

    onProgress: (percent: number) => Promise<void>;
    onStateChange: (
        state: "idle" | "uploading" | "paused" | "cancelling" | "completed" | "failed" | "cancelled"
    ) => Promise<void>;
};

/**
 * Coordinates temporary chunk allocations, bounded workers, pause/cancel and completion callbacks.
 * Preconditions: Callers supply usable chunk/concurrency settings, files and asynchronous callbacks.
 * Assumptions: Upload IDs refer to this instance's allocations; resume uses the same file and chunk
 * configuration. State is instance-local. Public promise fulfillment does not always mean completion.
 */
class ChunkedUploader {
    private concurrencyLimit: number;
    private maxRetries: number;
    private client: JsonServiceClient;
    private chunkSize: number;

    private uploadStates: Record<string, ChunkedUploadState>;
    private disposed = false;

    /**
     * Stores the client and worker/retry settings without runtime validation.
     * Preconditions: chunkSize and concurrencyLimit should be positive; maxRetries a nonnegative integer.
     * Postconditions: Starts undisposed with no tracked upload allocations.
     */
    constructor(
        client: JsonServiceClient,
        chunkSize = 5 * 1024 * 1024,
        concurrencyLimit = 3,
        maxRetries = 3
    ) {
        this.concurrencyLimit = concurrencyLimit;
        this.maxRetries = maxRetries;
        this.client = client;
        this.chunkSize = chunkSize;
        this.uploadStates = {};
    }

    /**
     * Requests a server allocation and records its local state when still undisposed.
     * Postconditions: A disposed instance returns undefined; a late allocation is removed when possible.
     * Request/cleanup failures return undefined, with disposed cleanup failures logged. Callbacks are stored
     * without an initial notification; chunk data is not sent here.
     */
    public createFileUpload = async (
        file: File,
        onProgress: (percent: number) => Promise<void>,
        onStateChange: (
            state:
                | "idle"
                | "uploading"
                | "paused"
                | "cancelling"
                | "completed"
                | "failed"
                | "cancelled"
        ) => Promise<void>
    ) => {
        if (this.disposed) {
            return undefined;
        }
        const request = new BeginTemporaryFileParallelChunkRequest({
            fileName: file.name,
            chunkSize: this.chunkSize,
            totalChunks: Math.ceil(file.size / this.chunkSize)
        });

        try {
            const response = await this.client.post(request);

            if (this.disposed) {
                await this.client.post(
                    new RemoveTemporaryFileParallelChunkRequest({
                        uploadId: response.uploadId
                    })
                );
                return undefined;
            }

            this.uploadStates[response.uploadId] = {
                activeControllers: new Set<AbortController>(),
                completedChunksCount: 0,
                isPaused: false,
                totalChunksCount: request.totalChunks,
                state: "idle",
                progress: 0,
                onProgress: onProgress,
                onStateChange: onStateChange
            };

            return response.uploadId;
        } catch (error) {
            if (this.disposed) {
                console.error("Failed to clean up a late chunk allocation:", error);
            }
            return undefined;
        }
    };

    /**
     * Reads server chunk status, returning undefined on any caught request failure.
     */
    public getUploadStatus = async (uploadId: string) => {
        const request = new GetTemporaryFileParallelChunkStatusRequest({
            uploadId: uploadId
        });

        try {
            const response = await this.client.get(request);

            return response;
        } catch {
            return undefined;
        }
    };

    /**
     * Pauses an actively uploading record and aborts its tracked requests before notifying.
     * Postconditions: Matching active state is marked paused and controllers cleared before the awaited
     * callback. Other states are unchanged; callback failures propagate after the local state change.
     */
    public pause = async (uploadId: string) => {
        const state = this.uploadStates[uploadId];

        if (state && !state.isPaused && state.state === "uploading") {
            state.isPaused = true;
            state.state = "paused";

            // Abort active requests before notifying the UI. The worker pool also
            // observes isPaused and will stop scheduling tasks between requests.
            for (const controller of state.activeControllers) {
                controller.abort();
            }

            state.activeControllers.clear();
            await state.onStateChange("paused");
        }
    };

    /**
     * Cancels a known upload or awaits its already-recorded cancellation promise.
     * Postconditions: Missing/already-cancelled uploads are no-ops. Concurrent callers sharing the stored
     * promise observe that cleanup result; the promise marker is cleared if the same state remains.
     * Cleanup and notification failures propagate, allowing the caller to report or retry them.
     */
    public cancel = async (uploadId: string, notifyStateChange = true) => {
        const state = this.uploadStates[uploadId];

        if (!state || state.state === "cancelled") {
            return;
        }

        if (state.cancellationPromise) {
            await state.cancellationPromise;
            return;
        }

        const cancellationPromise = this.cancelUpload(uploadId, state, notifyStateChange);
        state.cancellationPromise = cancellationPromise;

        try {
            await cancellationPromise;
        } finally {
            if (this.uploadStates[uploadId] === state) {
                state.cancellationPromise = undefined;
            }
        }
    };

    /**
     * Invalidates work, aborts controllers and removes the server allocation before discarding local state.
     * Postconditions: Cancelling is published before removal when enabled; successful removal publishes
     * cancelled then deletes local state. Removal or final-notification failure marks failed and rethrows.
     * Assumptions: The initial cancelling callback can throw before the removal try block runs.
     */
    private cancelUpload = async (
        uploadId: string,
        state: ChunkedUploadState,
        notifyStateChange: boolean
    ) => {
        // Invalidate completion callbacks before yielding to transport cleanup.
        state.isPaused = true;
        for (const controller of state.activeControllers) {
            controller.abort();
        }
        state.activeControllers.clear();

        state.state = "cancelling";
        if (notifyStateChange) {
            await state.onStateChange("cancelling");
        }

        try {
            await this.client.post(
                new RemoveTemporaryFileParallelChunkRequest({
                    uploadId
                })
            );

            state.state = "cancelled";
            if (notifyStateChange) {
                await state.onStateChange("cancelled");
            }

            delete this.uploadStates[uploadId];
        } catch (error) {
            state.state = "failed";
            if (notifyStateChange) {
                await state.onStateChange("failed");
            }

            throw error;
        }
    };

    /**
     * Marks the instance disposed and attempts sequential cancellation of its current allocations.
     * Postconditions: Per-upload failures are logged and do not stop later cleanup. Fulfillment does not
     * prove every server allocation was removed; late allocations are handled by createFileUpload.
     */
    public dispose = async () => {
        this.disposed = true;
        for (let key of Object.keys(this.uploadStates)) {
            try {
                await this.cancel(key, false);
            } catch (error) {
                console.error(`Failed to clean up cancelled upload ${key}:`, error);
            }
        }
    };

    /**
     * Resumes missing chunks from server status through the configured worker pool.
     * Preconditions: The file matches the registered allocation and chunk settings.
     * Postconditions: Stale/disposed/cancelling state returns early. Missing status/state throws. Already
     * complete status notifies progress then completion and removes local state. Otherwise completed
     * chunk IDs are skipped. Pool errors publish failed and are caught/logged; callback errors may escape.
     * Fulfillment can mean completion, pause or caught failure; it does not persist a source document.
     */
    public upload = async (file: File, uploadId: string) => {
        if (this.disposed) {
            return;
        }
        const expectedState = this.uploadStates[uploadId];
        const status = await this.getUploadStatus(uploadId);

        const state = this.uploadStates[uploadId];
        if (
            this.disposed ||
            state !== expectedState ||
            state?.cancellationPromise ||
            state?.state === "cancelling" ||
            state?.state === "cancelled"
        ) {
            return;
        }

        if (!status) {
            throw new Error("Could not retrieve upload status.");
        }

        if (!state) {
            throw new Error("Could not retrieve upload state.");
        }

        if (status.isComplete) {
            await state.onProgress(100);
            await state.onStateChange("completed");
            delete this.uploadStates[uploadId];
            return;
        }

        state.isPaused = false;
        state.state = "uploading";
        await state.onStateChange("uploading");

        const completedSet = new Set(status.completedChunks);
        state.completedChunksCount = completedSet.size;
        await this.updateProgress(state);

        const tasks: (() => Promise<void>)[] = [];

        for (let i = 0; i < status.totalChunks; i++) {
            if (completedSet.has(i)) {
                continue;
            } // Skip already completed chunks

            const start = i * this.chunkSize;
            const end = Math.min(start + this.chunkSize, file.size);
            const chunkBlob = file.slice(start, end);

            // Defer task execution to control concurrent workers
            tasks.push(() =>
                this.uploadChunkWithRetry(
                    chunkBlob,
                    uploadId,
                    i,
                    start,
                    file.size,
                    status.totalChunks
                )
            );
        }

        // Step 3: Run worker pool
        try {
            await this.runPool(state, tasks);

            if (!state.isPaused) {
                state.state = "completed";
                await state.onStateChange("completed");
                delete this.uploadStates[uploadId];
                return;
            }
        } catch (error) {
            state.state = "failed";
            await state.onStateChange("failed");
            console.error("Pool execution halted:", error);
        }
    };

    /**
     * Sends one chunk with abort-aware retries and reports completed-chunk progress.
     * Postconditions: Success increments completion before awaiting progress notification. Abort/pause
     * returns quietly; explicitly nonretryable errors escape. Other failures, including callback failures,
     * retry up to maxRetries after the initial attempt with exponential delays starting at one second.
     * Assumptions: The upload state exists. Backoff timers are not aborted; pause is checked next iteration.
     */
    private async uploadChunkWithRetry(
        blob: Blob,
        uploadId: string,
        index: number,
        offset: number,
        totalSize: number,
        totalChunks: number
    ): Promise<void> {
        let attempt = 0;
        const state = this.uploadStates[uploadId];

        while (attempt <= this.maxRetries) {
            // Guard clause: stop if the user hit pause between loop retries
            if (state.isPaused) {
                return;
            }

            // Generate an individual abort controller for this specific request block
            const controller = new AbortController();
            state.activeControllers.add(controller);

            try {
                const response = await fetch(
                    `/api/v2/temporary-file/upload-parallel-chunk`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/octet-stream",
                            "X-Upload-Id": uploadId,
                            "X-Total-Size": totalSize.toString(),
                            "X-Chunk-Size": blob.size.toString(),
                            "X-Chunk-Index": index.toString(),
                            "X-Total-Chunks": totalChunks.toString()
                        },
                        body: blob,
                        signal: controller.signal // Bind the abort flag here
                    }
                );

                if (!response.ok) {
                    const error = new Error(
                        `Chunk upload failed with status ${response.status}`
                    ) as Error & { retryable?: boolean };
                    error.retryable = isRetryableChunkUploadStatus(response.status);
                    throw error;
                }

                state.activeControllers.delete(controller);
                state.completedChunksCount++;
                await this.updateProgress(state);
                return;
            } catch (error: any) {
                state.activeControllers.delete(controller);

                // If error was explicitly caused by our pause() abort call, exit quietly
                if (error.name === "AbortError" || state.isPaused) {
                    return;
                }

                if (error.retryable === false) {
                    throw error;
                }

                attempt++;

                if (attempt > this.maxRetries) {
                    throw error;
                }

                const backoffMs = Math.pow(2, attempt) * 500;

                await new Promise(resolve => setTimeout(resolve, backoffMs));
            }
        }
    }

    /**
     * Schedules tasks up to the concurrency limit while observing pause between launches.
     * Postconditions: Normal fulfillment waits for tracked tasks. A rejection propagates from race/all;
     * this helper does not itself abort remaining requests or cancel server allocations.
     */
    private async runPool(
        state: ChunkedUploadState,
        tasks: (() => Promise<void>)[]
    ): Promise<void> {
        const executing = new Set<Promise<void>>();

        for (const task of tasks) {
            // Halt scheduling immediately if pause is toggled
            if (state.isPaused) {
                break;
            }

            const p = task().then(() => {
                executing.delete(p);
            }
            );
            executing.add(p);

            if (executing.size >= this.concurrencyLimit) {
                await Promise.race(executing);
            }
        }

        await Promise.all(executing);
    }

    /**
     * Notifies rounded completion percentage when the allocation has a nonzero total chunk count.
     * Postconditions: Zero totals skip notification; callback failures propagate. The result is not clamped
     * and this helper does not assign the state's progress property.
     */
    private updateProgress = async (state: ChunkedUploadState) => {
        if (state.totalChunksCount === 0) {
            return;
        }

        const pct = Math.round((state.completedChunksCount / state.totalChunksCount) * 100);

        await state.onProgress(pct);
    };
}

export default ChunkedUploader;
